import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const emptyArt = {
    codice: "", larghezza_cm: "", spessore_my: "", colore_nome: "",
    tipo_estrusione: "TUBOLARE", cliente_nome: "", additivi: "",
    stampa_colori: "", trattamento: "", note: "",
};

// Somma i termini "46+44" → 90; accetta anche un intero puro "90" → 90.
function largSum(v) {
    if (v == null) return 0;
    const s = String(v).trim();
    if (!s) return 0;
    return s.split("+").reduce((acc, p) => acc + (parseInt(p.trim(), 10) || 0), 0);
}

function pmlCalc(larg, spess, densita = 0.92) {
    const L = largSum(larg);
    const S = parseInt(spess) || 0;
    if (!L || !S) return 0;
    return Math.round((L * 2 * S * densita) / 100 - 2);
}

function suggestedCodice(f, colori) {
    // Il codice conserva la forma raw della larghezza per distinguere "TUB46+44N30"
    // da "TUB90N30" (soffietto già in estrusione).
    const largRaw = String(f.larghezza_cm || "").trim().replace(/\s+/g, "");
    const L = largSum(largRaw);
    const S = parseInt(f.spessore_my) || 0;
    if (!L || !S || !f.colore_nome) return "";
    const col = colori.find(c => (c.nome || "").toLowerCase() === (f.colore_nome || "").trim().toLowerCase());
    const cc = (col?.codice_breve || (f.colore_nome || "?")[0] || "?").toUpperCase();
    return `TUB${largRaw}${cc}${S}` + (f.tipo_estrusione === "MONOPIEGA" ? "M" : "");
}

export default function EstrusioneArticoli() {
    const { user, can } = useAuth();
    const canEdit = user?.role === "admin" || can("art_estr.edit");
    const [items, setItems] = useState([]);
    const [colori, setColori] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyArt);
    const [codiceTouched, setCodiceTouched] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [a, c] = await Promise.all([api.get("/articoli-estrusione"), api.get("/colori/")]);
            setItems(a.data); setColori(c.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditId(null); setForm(emptyArt); setCodiceTouched(false); setOpen(true); };
    const openEdit = (a) => {
        setEditId(a.id);
        setForm({
            codice: a.codice || "", larghezza_cm: a.larghezza_cm ?? "", spessore_my: a.spessore_my ?? "",
            colore_nome: a.colore_nome || "", tipo_estrusione: a.tipo_estrusione || "TUBOLARE",
            cliente_nome: a.cliente_nome || "", additivi: a.additivi || "",
            stampa_colori: a.stampa_colori || "", trattamento: a.trattamento || "", note: a.note || "",
        });
        setCodiceTouched(true);
        setOpen(true);
    };

    const sugg = useMemo(() => suggestedCodice(form, colori), [form.larghezza_cm, form.spessore_my, form.colore_nome, form.tipo_estrusione, colori]);
    const pml = useMemo(() => pmlCalc(form.larghezza_cm, form.spessore_my), [form.larghezza_cm, form.spessore_my]);
    const codiceShown = codiceTouched ? (form.codice || "") : (sugg || "");
    const codiceDiff = sugg && sugg !== (form.codice || "").toUpperCase();

    const save = async () => {
        if (!form.larghezza_cm || !form.spessore_my || !form.colore_nome || !form.tipo_estrusione) {
            toast.error("Larghezza, spessore, colore e tipo estrusione sono obbligatori"); return;
        }
        // Larghezza inviata come stringa raw ("90" o "46+44") — il backend somma per il PML
        // ma conserva la forma originale nel codice.
        const largRaw = String(form.larghezza_cm || "").trim().replace(/\s+/g, "");
        if (!/^\d+(\+\d+)*$/.test(largRaw)) {
            toast.error("Larghezza non valida. Usa un numero (es. 90) o una somma (es. 46+44)"); return;
        }
        const payload = {
            codice: (codiceShown || "").toUpperCase() || null,
            larghezza_cm: largRaw,
            spessore_my: parseInt(form.spessore_my) || 0,
            colore_nome: form.colore_nome.trim(),
            tipo_estrusione: form.tipo_estrusione,
            cliente_nome: form.cliente_nome?.trim() || null,
            additivi: form.additivi?.trim() || null,
            stampa_colori: form.stampa_colori?.trim() || null,
            trattamento: form.trattamento?.trim() || null,
            note: form.note?.trim() || null,
        };
        try {
            if (editId) { await api.put(`/articoli-estrusione/${editId}`, payload); toast.success("Articolo aggiornato"); }
            else { await api.post("/articoli-estrusione", payload); toast.success("Articolo creato"); }
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const del = async (a) => {
        if (!window.confirm(`Eliminare articolo ${a.codice}?`)) return;
        try { await api.delete(`/articoli-estrusione/${a.id}`); toast.success("Eliminato"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.3em] font-mono text-amber-400/80 flex items-center gap-2">
                        <Package size={12} /> ANAGRAFICA · ESTRUSIONE
                    </div>
                    <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Articoli Estrusione</h1>
                    <p className="mt-2 text-slate-400 text-sm max-w-2xl">Bobine estruse: larghezza, spessore, colore, tipo estrusione (TUBOLARE / MONOPIEGA).</p>
                </div>
                {canEdit && <Button onClick={openNew} data-testid="button-new-art-estr" className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                    <Plus size={16} /> Nuovo articolo estrusione
                </Button>}
            </div>

            <Card className="card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-900/60 text-[10px] uppercase tracking-widest text-slate-500">
                        <tr>
                            <th className="text-left px-5 py-3">Codice</th>
                            <th className="text-left px-5 py-3">Tipo</th>
                            <th className="text-right px-5 py-3">Larg. cm</th>
                            <th className="text-right px-5 py-3">Spess. μ</th>
                            <th className="text-left px-5 py-3">Colore</th>
                            <th className="text-left px-5 py-3">Cliente</th>
                            <th className="text-right px-5 py-3">PML (g)</th>
                            <th className="text-right px-5 py-3">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan="8" className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                        {!loading && items.length === 0 && <tr><td colSpan="8" className="px-5 py-8 text-center text-slate-500 text-xs">Nessun articolo estrusione</td></tr>}
                        {items.map(a => (
                            <tr key={a.id} className="border-t border-slate-800/60 hover:bg-slate-900/30" data-testid={`row-art-estr-${a.codice}`}>
                                <td className="px-5 py-3 font-mono text-amber-300 text-xs">{a.codice}</td>
                                <td className="px-5 py-3"><Badge className={`badge-pill ${a.tipo_estrusione === "MONOPIEGA" ? "bg-violet-500/15 text-violet-300 border-violet-500/40" : "bg-sky-500/15 text-sky-300 border-sky-500/40"}`}>{a.tipo_estrusione}</Badge></td>
                                <td className="px-5 py-3 text-right font-mono text-xs">{a.larghezza_cm}</td>
                                <td className="px-5 py-3 text-right font-mono text-xs">{a.spessore_my}</td>
                                <td className="px-5 py-3 text-xs">{a.colore_nome} <span className="text-slate-500 font-mono">· {a.colore_codice}</span></td>
                                <td className="px-5 py-3 text-xs text-slate-400">{a.cliente_nome || "—"}</td>
                                <td className="px-5 py-3 text-right font-mono text-xs text-emerald-300">{a.pml_gr}</td>
                                <td className="px-5 py-3 text-right whitespace-nowrap">
                                    {canEdit && (<>
                                        <button onClick={()=>openEdit(a)} data-testid={`button-edit-art-estr-${a.codice}`}
                                                className="p-2 rounded hover:bg-slate-700/50 text-slate-300"><Pencil size={14} /></button>
                                        <button onClick={()=>del(a)} data-testid={`button-del-art-estr-${a.codice}`}
                                                className="p-2 rounded hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                                    </>)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
                    <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica articolo estrusione" : "Nuovo articolo estrusione"}</DialogTitle></DialogHeader>
                    <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                        <div>
                            <Label className="text-xs text-slate-400">Codice articolo *</Label>
                            <Input data-testid="input-codice-estr" value={codiceShown}
                                   disabled={!!editId}
                                   onChange={e=>{ setCodiceTouched(true); setForm({...form, codice: e.target.value.toUpperCase()}); }}
                                   className={`bg-slate-950/50 border-slate-800 mt-1 font-mono ${editId ? "opacity-70" : ""}`} />
                            {editId && <div className="mt-1 text-[10px] text-slate-500 flex items-center gap-1"><Lock size={10} /> Codice bloccato dopo il primo salvataggio</div>}
                            {!editId && codiceDiff && <div className="mt-1 text-[10px] text-amber-400/80">Suggerito: {sugg}</div>}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div><Label className="text-xs text-slate-400">Larghezza (cm) *</Label>
                                <Input data-testid="input-larg-estr" type="text" inputMode="text"
                                       value={form.larghezza_cm}
                                       onChange={e=>setForm({...form, larghezza_cm: e.target.value})}
                                       placeholder="es. 90  oppure  46+44"
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" />
                                {form.larghezza_cm && form.larghezza_cm.includes("+") && (
                                    <div className="mt-1 text-[10px] text-emerald-400/80">
                                        Totale per PML: <span className="font-mono">{largSum(form.larghezza_cm)}</span> cm (soffietto già in estrusione)
                                    </div>
                                )}
                            </div>
                            <div><Label className="text-xs text-slate-400">Spessore (μ) *</Label>
                                <Input data-testid="input-spess-estr" type="number" value={form.spessore_my}
                                       onChange={e=>setForm({...form, spessore_my: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                            <div><Label className="text-xs text-slate-400">Tipo *</Label>
                                <Select value={form.tipo_estrusione} onValueChange={v=>setForm({...form, tipo_estrusione: v})}>
                                    <SelectTrigger data-testid="select-tipo-estr" className="bg-slate-950/50 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                        <SelectItem value="TUBOLARE">TUBOLARE</SelectItem>
                                        <SelectItem value="MONOPIEGA">MONOPIEGA</SelectItem>
                                    </SelectContent>
                                </Select></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs text-slate-400">Colore *</Label>
                                <Input data-testid="input-colore-estr" list="colori-datalist" value={form.colore_nome}
                                       onChange={e=>setForm({...form, colore_nome: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1" placeholder="es. Ambra" />
                                <datalist id="colori-datalist">
                                    {colori.map(c => <option key={c.id} value={c.nome} />)}
                                </datalist></div>
                            <div><Label className="text-xs text-slate-400">Cliente (opzionale)</Label>
                                <Input data-testid="input-cliente-estr" value={form.cliente_nome}
                                       onChange={e=>setForm({...form, cliente_nome: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1" placeholder="Cliente specifico o vuoto" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs text-slate-400">Additivi</Label>
                                <Input data-testid="input-add-estr" value={form.additivi}
                                       onChange={e=>setForm({...form, additivi: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1" placeholder="es. master UV 2%" /></div>
                            <div><Label className="text-xs text-slate-400">Stampa n° colori</Label>
                                <Input data-testid="input-stampa-estr" value={form.stampa_colori}
                                       onChange={e=>setForm({...form, stampa_colori: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1" placeholder="0 · 1 · 2 · NO" /></div>
                        </div>
                        <div><Label className="text-xs text-slate-400">Trattamento</Label>
                            <Input data-testid="input-tratt-estr" value={form.trattamento}
                                   onChange={e=>setForm({...form, trattamento: e.target.value})}
                                   className="bg-slate-950/50 border-slate-800 mt-1" placeholder="Testo libero" /></div>
                        <div><Label className="text-xs text-slate-400">Note</Label>
                            <Textarea data-testid="input-note-estr" rows={2} value={form.note}
                                      onChange={e=>setForm({...form, note: e.target.value})}
                                      className="bg-slate-950/50 border-slate-800 mt-1" /></div>
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3 text-xs text-emerald-300">
                            <b>PML calcolato:</b> <span className="font-mono text-base ml-2">{pml} g</span>
                            <div className="text-[10px] text-emerald-400/70 mt-1">= (larg × 2 × spess × 0,92) / 100 − 2</div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setOpen(false)} className="bg-transparent border-slate-700 text-slate-300">Annulla</Button>
                        <Button onClick={save} data-testid="button-save-art-estr" className="bg-amber-500 hover:bg-amber-400 text-slate-950">{editId ? "Aggiorna" : "Crea"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
